import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { useToast } from "@shared/hooks/use-toast";
import { Button } from "@shared/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@shared/ui/popover";
import { Calendar } from "@shared/ui/calendar";
import { format } from "date-fns";
import { Calendar1Icon, Calendar as CalendarIcon, Eye, CheckCircle, PlayCircle } from "lucide-react";
import { GenericTable } from "../../../../../shared/components/GenericTable";
import { TableColumn, TableAction } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import {
  useDistribution,
  useDistributionExport,
  useUpdatePrintStatus,
} from "../../hooks";
import { StatusBadge } from "./StatusBadge";

interface DistributionTableProps {
  activeFilter: string | null;
  onCycleClick: (cycle: any) => void;
}

interface CycleData {
  totalBillCount: number;
  eBillCount: number;
  paperBillCount: number;
  deliveredEBillCount: number;
  failedEBillCount: number;
  deliveredPaperBill: number;
  lapsedDays: number;
  runId: number;
  cycleName: string;
  cycleId: number;
  invoiceStartDate: string;
  invoiceEndDate: string;
  dispatchedDate: string;
  dispatchStatus: string | null;
}

export function DistributionTable({
  activeFilter,
  onCycleClick,
}: DistributionTableProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Read URL params
  const searchQuery = searchParams.get("query") || "";
  const currentPage = parseInt(searchParams.get("page") || "1");

  const [editingCycleId, setEditingCycleId] = useState<number | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [currentRow, setCurrentRow] = useState<any>(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  // Build API filters
  const apiFilters = {
    page: currentPage,
    limit: PAGE_SIZE,
    ...(searchQuery && { search_data: searchQuery }),
    ...(activeFilter && { active_filter: activeFilter }),
  };

  const { data: distributionData } = useDistribution(apiFilters);

  // Export state
  const exportFilters = { ...apiFilters };
  delete exportFilters.page;
  delete exportFilters.limit;
  const { exportCSV, isExporting } = useDistributionExport(exportFilters);

  const updatePrintStatus = useUpdatePrintStatus();

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: PAGE_SIZE,
    startItem: 0,
    endItem: 0,
  });

  // Update pagination based on API data
  useEffect(() => {
    const totalItems = distributionData?.count || 0;
    const totalPages = Math.ceil(totalItems / PAGE_SIZE);
    const startItem = (currentPage - 1) * PAGE_SIZE + 1;
    const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

    setPagination({
      currentPage: currentPage,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: totalItems > 0 ? startItem : 0,
      endItem: totalItems > 0 ? endItem : 0,
    });
  }, [distributionData?.count, currentPage]);

  // Handle search
  const handleSearch = (searchTerm: string) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
    };

    if (searchTerm.trim()) {
      newParams.query = searchTerm.trim();
    } else {
      delete newParams.query;
    }

    // Reset to page 1 on new search
    delete newParams.page;

    setSearchParams(newParams);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      page: page.toString(),
    };
    setSearchParams(newParams);
  };

  // Handle export
  const handleExport = () => {
    exportCSV();
  };

  // Handle date selection and call API immediately
  const handleDateChange = (date: Date | undefined) => {
    if (!date || !currentRow) return;

    const formattedDate = format(date, "yyyy-MM-dd");

    updatePrintStatus.mutate(
      {
        runId: currentRow.runId,
        payload: {
          dispatch_date: formattedDate,
          dispatch_status: currentRow.dispatchStatus,
        },
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey:
              QueryKeyFactory.module.bx.printingDistribution.distributionList(),
          });
          toast({
            title: "Success",
            description: "Dispatch date updated successfully",
          });
          setEditingCycleId(null);
          setSelectedDate(null);
          setCurrentRow(null);
          setIsPopoverOpen(false);
        },
        onError: () => {
          toast({
            title: "Error",
            description: "Failed to update dispatch date",
            variant: "destructive",
          });
          setEditingCycleId(null);
          setSelectedDate(null);
          setCurrentRow(null);
          setIsPopoverOpen(false);
        },
      }
    );
  };

  const handleEditDate = (row: any) => {
    setEditingCycleId(row.runId);
    setCurrentRow(row);
    setIsPopoverOpen(true);
    // Pre-populate with existing date if available
    if (row.dispatchedDate) {
      try {
        const parsedDate = new Date(row.dispatchedDate);
        if (!isNaN(parsedDate.getTime())) {
          setSelectedDate(parsedDate);
        } else {
          setSelectedDate(null);
        }
      } catch {
        setSelectedDate(null);
      }
    } else {
      setSelectedDate(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingCycleId(null);
    setSelectedDate(null);
    setCurrentRow(null);
    setIsPopoverOpen(false);
  };

  const handleViewConsumers = (row: any) => {
    onCycleClick(row);
    navigate(`/bx/printing-distribution/consumers/${row.runId}`);
  };

  const handleUpdateStatus = (row: any, newStatus: string) => {
    let formattedDate = undefined;
    if (row.dispatchedDate) {
      try {
        const parsed = new Date(row.dispatchedDate);
        if (!isNaN(parsed.getTime())) {
          formattedDate = format(parsed, "yyyy-MM-dd");
        }
      } catch {
        // Ignore invalid dates
      }
    }

    updatePrintStatus.mutate(
      {
        runId: row.runId,
        payload: {
          dispatch_date: formattedDate,
          dispatch_status: newStatus,
          ...(newStatus === "Completed" && { dispatch_complete_date: format(new Date(), "yyyy-MM-dd") }),
        },
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: QueryKeyFactory.module.bx.printingDistribution.distributionList(),
          });
          toast({
            title: "Success",
            description: `Status updated to ${newStatus}`,
          });
        },
      }
    );
  };

  const tableActions: TableAction<any>[] = [
    {
      label: "Mark Started",
      icon: <PlayCircle className="w-4 h-4" />,
      onClick: (row) => handleUpdateStatus(row, "Started"),
      hidden: (row) => {
        const status = row.dispatchStatus || "Pending";
        return status !== "Pending" && status !== "Not Started";
      },
    },
    {
      label: "Mark Completed",
      icon: <CheckCircle className="w-4 h-4" />,
      onClick: (row) => handleUpdateStatus(row, "Completed"),
      hidden: (row) => {
        const status = row.dispatchStatus;
        return status !== "Started" && status !== "In Progress";
      },
    },
    {
      label: "Edit Dispatch Date",
      icon: <CalendarIcon className="w-4 h-4" />,
      onClick: (row) => handleEditDate(row),
    },
    {
      label: "View Consumers",
      icon: <Eye className="w-4 h-4" />,
      onClick: (row) => handleViewConsumers(row),
    },
  ];

  // Define table columns
  const columns: TableColumn<any>[] = [
    {
      key: "cycleName",
      header: "Billing Cycle",
      render: (value: string) => <span className="font-medium">{value}</span>,
    },
    {
      key: "period",
      header: "Period",
      render: (value: any, row: CycleData) => {
        const startRaw = row?.invoiceStartDate;
        const endRaw = row?.invoiceEndDate;

        if (!startRaw || !endRaw) return "-";

        return `${startRaw} - ${endRaw}`;
      },
    },
    {
      key: "totalBillCount",
      header: "Total",
      render: (value: number) => (
        <span className="text-right">{value.toLocaleString()}</span>
      ),
    },
    {
      key: "dispatchedDate",
      header: "Dispatch Date",
      render: (value: string, row: CycleData) => {
        const isEditing = editingCycleId === row.runId;

        return (
          <Popover
            open={isEditing && isPopoverOpen}
            onOpenChange={(open) => {
              if (!open) {
                handleCancelEdit();
              }
            }}
          >
            <PopoverTrigger asChild>
              <span
                className="flex items-center gap-2 space-x-2 "
              >
                {value || "N/A"}
              </span>
            </PopoverTrigger>
            <PopoverContent 
              className="w-auto p-0" 
              align="start"
              onFocusOutside={(e) => e.preventDefault()}
            >
              <Calendar
                mode="single"
                selected={selectedDate || undefined}
                onSelect={handleDateChange}
                initialFocus
                className="rounded-md border"
              />
            </PopoverContent>
          </Popover>
        );
      },
    },
    {
      key: "eBillCount",
      header: "E-Bill",
      render: (value: any, row: CycleData) => (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-xs font-medium text-blue-500">
                {row?.eBillCount}/{row?.deliveredEBillCount}/
                {row?.failedEBillCount}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <div className="space-y-1">
                <div>
                  <span className="font-semibold">Opted:</span>{" "}
                  {row?.eBillCount || 0}
                </div>
                <div>
                  <span className="font-semibold">Delivered:</span>{" "}
                  {row?.deliveredEBillCount || 0}
                </div>
                <div>
                  <span className="font-semibold">Failed:</span>{" "}
                  {row?.failedEBillCount || 0}
                </div>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ),
    },
    {
      key: "paperBillCount",
      header: "Paper",
      render: (value: any, row: CycleData) => (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-xs font-medium text-amber-500">
                {row?.paperBillCount}/{row?.deliveredPaperBill}/0
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <div className="space-y-1">
                <div>
                  <span className="font-semibold">Opted:</span>{" "}
                  {row?.paperBillCount || 0}
                </div>
                <div>
                  <span className="font-semibold">Delivered:</span>{" "}
                  {row?.deliveredPaperBill || 0}
                </div>
                <div>
                  <span className="font-semibold">Failed:</span> 0
                </div>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ),
    },
    {
      key: "dispatchStatus",
      header: "Status",
      render: (value: string | null) => {
        const displayValue = value || "Not Started";
        let statusType: any = "pending";
        if (displayValue === "Completed") statusType = "completed";
        else if (displayValue === "In Progress" || displayValue === "Started") statusType = "Started";
        else if (displayValue === "Failed") statusType = "failed";

        return <StatusBadge status={statusType} label={displayValue} />;
      },
    },
    {
      key: "lapsedDays",
      header: "Lapsed Days",
      render: (value: number) => {
        if (value === 0) return "-";
        return <StatusBadge status="warning" label={`${value} days`} />;
      },
    },
  ];

  return (
    <div className="space-y-6 mt-6">
      <GenericTable
        data={distributionData?.result || []}
        columns={columns}
        rowKey="runId"
        emptyMessage="No distribution cycles found"
        pagination={pagination}
        onPageChange={handlePageChange}
        onSearch={handleSearch}
        searchPlaceholder="Search cycles..."
        actions={tableActions}
        actionsType="dropdown"
        onExport={handleExport}
        exportLoading={isExporting}
      />
    </div>
  );
}
